import React from 'react'
import { Permissions } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { api, canonicalStoredFileIdForApiPath } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import { useToast } from '../ui/ToastProvider'
import { SECTION_TEMPLATES } from '../features/orgHomepage/registry'

const DEFAULT_FORM = {
  id: null,
  sectionType: 'hero',
  slotKey: 'backgroundImage',
  assetCategory: 'SECTION_LIBRARY',
  name: '',
  description: '',
  tags: [],
  previewImageFileId: '',
  imageFileId: '',
  isActive: true,
  sortOrder: 0,
}

const ASSET_CATEGORY_OPTIONS = [
  { value: 'TEMPLATE_SAMPLE', label: '기본 템플릿 이미지' },
  { value: 'SECTION_LIBRARY', label: '운영 라이브러리 이미지' },
]

const SLOT_OPTIONS = {
  hero: ['backgroundImage'],
  'ga-intro': ['mainImage'],
  'ceo-message': ['mainImage'],
  'business-intro': ['mainImage'],
  'lead-form': ['sideImage'],
}

function getPreviewImageUrl(fileId) {
  if (!fileId) return ''
  const raw = String(fileId)
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) {
    return raw
  }
  const apiBase = (api?.defaults?.baseURL || '').replace(/\/$/, '')
  return `${apiBase}/files/${encodeURIComponent(canonicalStoredFileIdForApiPath(raw))}/preview`
}

export default function OrgHomepageAssetsPage() {
  const { show } = useToast()
  const { allow } = useAccess()
  const canManage = allow.perm([Permissions.TEMPLATE_MANAGE])
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [items, setItems] = React.useState([])
  const [form, setForm] = React.useState(DEFAULT_FORM)
  const [categoryFilter, setCategoryFilter] = React.useState('ALL')

  const reload = React.useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/org-landing-section-assets')
      const payload = res?.data?.data || res?.data
      setItems(Array.isArray(payload?.items) ? payload.items : [])
    } catch {
      show('조직 홈페이지 이미지 라이브러리를 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }, [show])

  React.useEffect(() => {
    reload()
  }, [reload])

  const uploadImage = async (file, key) => {
    if (!file) return
    try {
      const body = new FormData()
      body.append('file', file)
      const res = await api.post('/files/upload/public-landing-image', body)
      const fileId = res?.data?.data?.fileId || res?.data?.fileId || ''
      if (!fileId) throw new Error('fileId not found')
      setForm((prev) => ({ ...prev, [key]: fileId }))
      show('이미지가 업로드되었습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || err?.message || '업로드 실패', 'error')
    }
  }

  const onSave = async () => {
    if (!canManage) return
    if (!form.name || !form.imageFileId) {
      show('이름과 실사용 이미지는 필수입니다.', 'error')
      return
    }
    setSaving(true)
    try {
      const body = {
        sectionType: form.sectionType,
        slotKey: form.slotKey,
        assetCategory: form.assetCategory,
        name: form.name,
        description: form.description,
        tags: form.tags,
        previewImageFileId: form.previewImageFileId || form.imageFileId,
        imageFileId: form.imageFileId,
        isActive: form.isActive,
        sortOrder: Number(form.sortOrder || 0),
      }
      if (form.id) {
        await api.patch(`/org-landing-section-assets/${form.id}`, body)
      } else {
        await api.post('/org-landing-section-assets', body)
      }
      show('이미지 라이브러리가 저장되었습니다.', 'success')
      setForm(DEFAULT_FORM)
      reload()
    } catch (err) {
      show(err?.response?.data?.message || err?.message || '저장 실패', 'error')
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async (id) => {
    if (!canManage) return
    if (!window.confirm('정말 삭제하시겠습니까?')) return
    try {
      await api.delete(`/org-landing-section-assets/${id}`)
      show('삭제되었습니다.', 'success')
      if (form.id === id) setForm(DEFAULT_FORM)
      reload()
    } catch {
      show('삭제 실패', 'error')
    }
  }

  const visibleItems = React.useMemo(() => (
    categoryFilter === 'ALL'
      ? items
      : items.filter((item) => String(item.assetCategory || 'SECTION_LIBRARY') === categoryFilter)
  ), [categoryFilter, items])

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">템플릿관리 &gt; 조직홈페이지 이미지</h1>
            <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
          </div>
        </div>
        <button type="button" className="rounded border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100" onClick={() => setForm(DEFAULT_FORM)}>새 이미지</button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[420px_minmax(0,1fr)]">
        <div className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
          <div>
            <div className="text-sm font-semibold text-slate-800">{form.id ? '이미지 수정' : '이미지 등록'}</div>
            <div className="mt-1 text-xs text-slate-500">기본 템플릿 이미지는 전역 기본값이며, 실제 조직 편집 화면에서는 다른 이미지로 개별 변경 가능합니다.</div>
          </div>
          <div className="space-y-4">
            <select
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              value={form.sectionType}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                sectionType: e.target.value,
                slotKey: SLOT_OPTIONS[e.target.value]?.[0] || 'backgroundImage',
              }))}
            >
              {SECTION_TEMPLATES.map((item) => <option key={item.type} value={item.type}>{item.label}</option>)}
            </select>
            <select
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              value={form.slotKey}
              onChange={(e) => setForm((prev) => ({ ...prev, slotKey: e.target.value }))}
            >
              {(SLOT_OPTIONS[form.sectionType] || ['backgroundImage']).map((slot) => <option key={slot} value={slot}>{slot}</option>)}
            </select>
            <select
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              value={form.assetCategory}
              onChange={(e) => setForm((prev) => ({ ...prev, assetCategory: e.target.value }))}
            >
              {ASSET_CATEGORY_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <input className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" placeholder="운영자 표시명" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
            <textarea rows={3} className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" placeholder="설명" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
            <input
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              placeholder="태그 (쉼표로 구분)"
              value={(form.tags || []).join(', ')}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                tags: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
              }))}
            />
            <div className="grid gap-4 md:grid-cols-2">
              <label className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                미리보기 업로드
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], 'previewImageFileId')} />
              </label>
              <label className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                실사용 이미지 업로드
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], 'imageFileId')} />
              </label>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {form.previewImageFileId ? <img src={getPreviewImageUrl(form.previewImageFileId)} alt="preview" className="h-32 w-full rounded-2xl object-cover" /> : <div className="flex h-32 items-center justify-center rounded-2xl border border-dashed text-xs text-slate-400">미리보기 없음</div>}
              {form.imageFileId ? <img src={getPreviewImageUrl(form.imageFileId)} alt="실사용 미리보기" className="h-32 w-full rounded-2xl object-cover" /> : <div className="flex h-32 items-center justify-center rounded-2xl border border-dashed text-xs text-slate-400">실사용 이미지 없음</div>}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />
                활성
              </label>
              <input className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" type="number" placeholder="정렬순서" value={form.sortOrder} onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))} />
            </div>
            <button type="button" className="w-full rounded-2xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={!canManage || saving} onClick={onSave}>
              {saving ? '저장 중...' : form.id ? '수정 저장' : '등록 저장'}
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button type="button" className={`rounded-full px-3 py-1 text-xs ${categoryFilter === 'ALL' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`} onClick={() => setCategoryFilter('ALL')}>전체</button>
              {ASSET_CATEGORY_OPTIONS.map((item) => (
                <button
                  type="button"
                  key={item.value}
                  className={`rounded-full px-3 py-1 text-xs ${categoryFilter === item.value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                  onClick={() => setCategoryFilter(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <button type="button" className="rounded border px-4 py-2 text-sm" onClick={reload}>{loading ? '불러오는 중...' : '새로고침'}</button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleItems.map((item) => (
              <div key={item.id} className="overflow-hidden rounded-3xl border border-slate-200">
                <img src={getPreviewImageUrl(item.previewImageFileId || item.imageFileId)} alt={item.name} className="aspect-[4/3] w-full object-cover" />
                <div className="space-y-2 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{item.sectionType} / {item.slotKey}</div>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${item.assetCategory === 'TEMPLATE_SAMPLE' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>
                      {item.assetCategory === 'TEMPLATE_SAMPLE' ? '기본 템플릿' : '운영 라이브러리'}
                    </span>
                  </div>
                  <div className="text-base font-bold text-slate-900">{item.name}</div>
                  {item.description ? <div className="text-sm leading-6 text-slate-500">{item.description}</div> : null}
                  <div className="flex flex-wrap gap-2">
                    {(item.tags || []).map((tag) => <span key={`${item.id}-${tag}`} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">{tag}</span>)}
                  </div>
                  <div className="flex items-center justify-between pt-2 text-xs text-slate-400">
                    <span>{item.isActive ? '활성' : '비활성'} / 순서 {item.sortOrder}</span>
                    <div className="flex items-center gap-2">
                      <button type="button" className="text-blue-600 hover:underline" onClick={() => setForm({ ...DEFAULT_FORM, ...item, assetCategory: item.assetCategory || 'SECTION_LIBRARY', tags: item.tags || [] })}>수정</button>
                      <button type="button" className="text-red-600 hover:underline" onClick={() => onDelete(item.id)}>삭제</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {visibleItems.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                아직 등록된 이미지가 없습니다.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}
